
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Trash2, MessageSquare, Plus } from "lucide-react";
import { useCustomerNotes } from "@/hooks/useCustomerNotes";
import { format } from "date-fns";

interface CustomerNotesSectionProps {
  providerId: string;
  customerEmail: string;
}

const CustomerNotesSection = ({ providerId, customerEmail }: CustomerNotesSectionProps) => {
  const [newNote, setNewNote] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  
  const { 
    notes, 
    isLoading, 
    addNote, 
    deleteNote, 
    isAddingNote 
  } = useCustomerNotes(providerId, customerEmail);

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    
    addNote({ noteContent: newNote.trim() });
    setNewNote("");
    setShowAddForm(false);
  };

  const handleDeleteNote = (noteId: string) => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      deleteNote(noteId);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Notes
        </h3>
        
        {!showAddForm && (
          <Button 
            onClick={() => setShowAddForm(true)} 
            size="sm"
            variant="outline"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Note
          </Button>
        )}
      </div>

      {showAddForm && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base">Add New Note</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              placeholder="Enter your note about this customer..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
            />
            <div className="flex gap-2">
              <Button 
                onClick={handleAddNote} 
                disabled={!newNote.trim() || isAddingNote}
                size="sm"
              >
                {isAddingNote ? "Adding..." : "Add Note"}
              </Button>
              <Button 
                onClick={() => {
                  setShowAddForm(false);
                  setNewNote("");
                }}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {notes && notes.length > 0 ? (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {notes.map((note, index) => (
            <div key={note.id}>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {note.note_content}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {formatDate(note.created_at)}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleDeleteNote(note.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
              {index < notes.length - 1 && <Separator className="my-2" />}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
          <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p>No notes for this customer yet</p>
          <p className="text-sm mt-1">Add a note to keep track of important information</p>
        </div>
      )}
    </div>
  );
};

export default CustomerNotesSection;
